import React, { useState } from 'react';
import {
    Autocomplete,
    Badge,
    Box,
    Button,
    Divider,
    Drawer,
    IconButton,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSelector } from 'react-redux';
import Loader from 'components/common/Loader';
import { Filter, X } from 'lucide-react';

const isValidDate = date => date instanceof Date && !Number.isNaN(date.getTime());

const statusOptions = [
    { label: 'Upcoming', value: 'upcoming' },
    { label: 'Active', value: 'active' },
    { label: 'Expired', value: 'expired' },
    { label: 'Canceled', value: 'canceled' }
];

const selectSx = {
    height: 32,
    fontSize: 13,
    bgcolor: 'white',
    borderRadius: '4px',
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#d9d9d9'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#b0b0b0'
    },
    '& .MuiSelect-select': {
        py: '4px',
        px: '10px'
    }
};

const autocompleteSx = {
    flex: '1 1 180px',
    minWidth: 0,
    maxWidth: { xs: 'none', sm: 260 },
    '& .MuiOutlinedInput-root': {
        height: 32,
        fontSize: 13,
        bgcolor: 'white',
        borderRadius: '4px',
        py: '0 !important',
        pl: '10px !important',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d9d9d9'
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#b0b0b0'
        }
    },
    '& .MuiAutocomplete-input': {
        p: '0 !important'
    },
    '& input::placeholder': {
        color: '#8c8c8c',
        opacity: 1
    },
    '& .MuiSvgIcon-root': {
        color: '#33c78f'
    }
};

const datePickerSx = {
    flex: 1,
    minWidth: 0,
    '& .MuiInputBase-root': {
        height: 32,
        fontSize: 13,
        bgcolor: 'white',
        borderRadius: '4px',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d9d9d9'
        }
    },
    '& input': {
        py: '4px',
        px: '10px'
    },
    '& .MuiSvgIcon-root': {
        color: '#33c78f'
    }
};

export default function RentalFilters({
    onSearch,
    onReset,
    defaultDates,
    showPropertyFilter = true,
    propertyOptions = []
}) {
    const loading = useSelector(state => state.access.loading);

    const [local, setLocal] = useState({
        ...defaultDates,
        status: '',
        propertyId: ''
    });

    const [touched, setTouched] = useState(false);
    const [errors, setErrors] = useState({});
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    const hasFilters =
        local.propertyId !== '' ||
        local.status !== '' ||
        local.from?.getTime?.() !== defaultDates.from?.getTime?.() ||
        local.to?.getTime?.() !== defaultDates.to?.getTime?.();

    const handleChange = (key, value) => {
        if (key === 'from' || key === 'to') {
            setErrors(prev => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        }

        setLocal(prev => ({ ...prev, [key]: value }));
    };

    const handleClear = () => {
        const cleared = {
            ...defaultDates,
            status: '',
            propertyId: ''
        };

        setLocal(cleared);
        setTouched(false);
        setErrors({});
        onReset();
        setMobileFiltersOpen(false);
    };

    const handleSearch = () => {
        setTouched(true);

        const nextErrors = {};

        if (local.from && !isValidDate(local.from)) {
            nextErrors.from = 'Invalid date.';
        }

        if (local.to && !isValidDate(local.to)) {
            nextErrors.to = 'Invalid date.';
        }

        setErrors(nextErrors);

        if (!Object.keys(nextErrors).length) {
            onSearch(local);
            setMobileFiltersOpen(false);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            {loading && <Loader />}

            <Box>
                {/* Mobile filter trigger */}
                <Box
                    sx={{
                        display: { xs: 'flex', sm: 'none' },
                        alignItems: 'center',
                        px: '20px',
                        py: '15px'
                    }}
                >
                    <Badge color="success" variant="dot" invisible={!hasFilters}>
                        <Button
                            variant="outlined"
                            startIcon={<Filter size={14} />}
                            onClick={() => setMobileFiltersOpen(true)}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px',
                                color: 'text.primary',
                                borderColor: '#D9D9D9',
                                fontSize: 13,
                                px: 2,
                                py: '5px',
                                height: 32
                            }}
                        >
                            Filters
                        </Button>
                    </Badge>
                </Box>

                {/* Desktop / tablet filter bar */}
                <Box
                    sx={{
                        display: { xs: 'none', sm: 'flex' },
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'stretch', md: 'center' },
                        gap: '10px',
                        px: '20px',
                        py: '15px',
                        width: '100%'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: '10px',
                            flex: 1,
                            minWidth: 0
                        }}
                    >
                        {showPropertyFilter && (
                            <Autocomplete
                                size="small"
                                options={propertyOptions}
                                getOptionLabel={option => option.label || ''}
                                isOptionEqualToValue={(option, value) =>
                                    option.value === value.value
                                }
                                value={
                                    propertyOptions.find(
                                        option => option.value === local.propertyId
                                    ) || null
                                }
                                onChange={(event, newValue) =>
                                    handleChange('propertyId', newValue?.value || '')
                                }
                                sx={autocompleteSx}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        placeholder="Property address"
                                    />
                                )}
                            />
                        )}

                        <Select
                            value={local.status}
                            onChange={e => handleChange('status', e.target.value)}
                            displayEmpty
                            renderValue={value =>
                                value ? (
                                    statusOptions.find(option => option.value === value)
                                        ?.label || value
                                ) : (
                                    <Typography
                                        component="span"
                                        sx={{ color: '#8c8c8c', fontSize: 13 }}
                                    >
                                        Status
                                    </Typography>
                                )
                            }
                            sx={{
                                ...selectSx,
                                flex: '1 1 130px',
                                minWidth: 0,
                                maxWidth: { xs: 'none', sm: 220 }
                            }}
                        >
                            <MenuItem value="">All statuses</MenuItem>
                            {statusOptions.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                flex: '1 1 200px',
                                minWidth: 0,
                                maxWidth: { xs: 'none', sm: 280 }
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    color: '#212322',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                From:
                            </Typography>

                            <DateTimePicker
                                closeOnSelect={false}
                                value={local.from}
                                onChange={date => handleChange('from', date ?? null)}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        placeholder: 'Select date',
                                        error: touched && Boolean(errors.from),
                                        helperText: touched && errors.from,
                                        sx: datePickerSx
                                    }
                                }}
                            />
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                flex: '1 1 200px',
                                minWidth: 0,
                                maxWidth: { xs: 'none', sm: 280 }
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    color: '#212322',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                To:
                            </Typography>

                            <DateTimePicker
                                closeOnSelect={false}
                                value={local.to}
                                onChange={date => handleChange('to', date ?? null)}
                                minDate={local.from}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        placeholder: 'Select date',
                                        error: touched && Boolean(errors.to),
                                        helperText: touched && errors.to,
                                        sx: datePickerSx
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '10px',
                            width: { xs: '100%', md: 'auto' },
                            ml: { xs: 0, md: 'auto' },
                            flexShrink: 0
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={handleSearch}
                            sx={{
                                flex: { xs: 1, md: '0 0 auto' },
                                bgcolor: '#33c78f',
                                color: 'white',
                                fontSize: 13,
                                borderRadius: '8px',
                                px: 3,
                                py: '5px',
                                textTransform: 'none',
                                boxShadow: 'none',
                                height: 32,
                                '&:hover': {
                                    bgcolor: '#28b07d',
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            Apply
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<X size={14} />}
                            disabled={!hasFilters}
                            onClick={handleClear}
                            sx={{
                                flex: { xs: 1, md: '0 0 auto' },
                                bgcolor: hasFilters ? 'white' : '#f5f5f5',
                                color: hasFilters ? '#596470' : '#d9d9d9',
                                borderColor: hasFilters ? '#d9d9d9' : '#d9d9d9',
                                fontSize: 13,
                                borderRadius: '8px',
                                px: '10px',
                                py: '5px',
                                textTransform: 'none',
                                height: 32,
                                minWidth: { xs: 0, md: 96 },
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: hasFilters ? '#f8f8f8' : '#f5f5f5',
                                    borderColor: '#d9d9d9'
                                },
                                '&.Mui-disabled': {
                                    bgcolor: '#f5f5f5',
                                    color: '#d9d9d9',
                                    borderColor: '#d9d9d9'
                                }
                            }}
                        >
                            Reset
                        </Button>
                    </Box>
                </Box>

                <Divider sx={{ borderColor: '#f0f0f0' }} />
            </Box>

            {/* Mobile filter drawer */}
            <Drawer
                anchor="bottom"
                open={mobileFiltersOpen}
                onClose={() => setMobileFiltersOpen(false)}
                PaperProps={{
                    sx: {
                        borderTopLeftRadius: '16px',
                        borderTopRightRadius: '16px',
                        maxHeight: '85vh'
                    }
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 3,
                        pt: 2.5,
                        pb: 1.5
                    }}
                >
                    <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                        Filters
                    </Typography>

                    <IconButton
                        size="small"
                        onClick={() => setMobileFiltersOpen(false)}
                    >
                        <X size={18} />
                    </IconButton>
                </Box>

                <Divider sx={{ borderColor: '#f0f0f0' }} />

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        p: 3,
                        overflowY: 'auto'
                    }}
                >
                    {showPropertyFilter && (
                        <Autocomplete
                            size="small"
                            options={propertyOptions}
                            getOptionLabel={option => option.label || ''}
                            isOptionEqualToValue={(option, value) =>
                                option.value === value.value
                            }
                            value={
                                propertyOptions.find(
                                    option => option.value === local.propertyId
                                ) || null
                            }
                            onChange={(event, newValue) =>
                                handleChange('propertyId', newValue?.value || '')
                            }
                            sx={{ ...autocompleteSx, flex: 'unset', width: '100%' }}
                            renderInput={params => (
                                <TextField {...params} placeholder="Property address" />
                            )}
                        />
                    )}

                    <Select
                        value={local.status}
                        onChange={e => handleChange('status', e.target.value)}
                        displayEmpty
                        renderValue={value =>
                            value ? (
                                statusOptions.find(option => option.value === value)
                                    ?.label || value
                            ) : (
                                <Typography
                                    component="span"
                                    sx={{ color: '#8c8c8c', fontSize: 13 }}
                                >
                                    Status
                                </Typography>
                            )
                        }
                        sx={{ ...selectSx, width: '100%' }}
                    >
                        <MenuItem value="">All statuses</MenuItem>
                        {statusOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>

                    <DateTimePicker
                        closeOnSelect={false}
                        value={local.from}
                        onChange={date => handleChange('from', date ?? null)}
                        slotProps={{
                            textField: {
                                size: 'small',
                                placeholder: 'From date',
                                error: touched && Boolean(errors.from),
                                helperText: touched && errors.from,
                                sx: { ...datePickerSx, flex: 'unset', width: '100%' }
                            }
                        }}
                    />

                    <DateTimePicker
                        closeOnSelect={false}
                        value={local.to}
                        onChange={date => handleChange('to', date ?? null)}
                        minDate={local.from}
                        slotProps={{
                            textField: {
                                size: 'small',
                                placeholder: 'To date',
                                error: touched && Boolean(errors.to),
                                helperText: touched && errors.to,
                                sx: { ...datePickerSx, flex: 'unset', width: '100%' }
                            }
                        }}
                    />
                </Box>

                <Divider sx={{ borderColor: '#f0f0f0' }} />

                <Box
                    sx={{
                        display: 'flex',
                        gap: '10px',
                        p: 2
                    }}
                >
                    <Button
                        variant="outlined"
                        startIcon={<X size={14} />}
                        disabled={!hasFilters}
                        onClick={handleClear}
                        sx={{
                            flex: 1,
                            bgcolor: hasFilters ? 'white' : '#f5f5f5',
                            color: hasFilters ? '#596470' : '#d9d9d9',
                            borderColor: '#d9d9d9',
                            fontSize: 13,
                            borderRadius: '8px',
                            textTransform: 'none',
                            height: 40,
                            '&.Mui-disabled': {
                                bgcolor: '#f5f5f5',
                                color: '#d9d9d9',
                                borderColor: '#d9d9d9'
                            }
                        }}
                    >
                        Reset
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleSearch}
                        sx={{
                            flex: 1,
                            bgcolor: '#33c78f',
                            color: 'white',
                            fontSize: 13,
                            borderRadius: '8px',
                            textTransform: 'none',
                            boxShadow: 'none',
                            height: 40,
                            '&:hover': {
                                bgcolor: '#28b07d',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        Apply
                    </Button>
                </Box>
            </Drawer>
        </LocalizationProvider>
    );
}
